import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import Wordmark from "../site/Wordmark";

export const NAV = [
  ["/app/dashboard", "Dashboard", "LayoutDashboard"],
  ["/app/crm", "CRM", "Database"],
  ["/app/leads", "Leads", "Target"],
  ["/app/customers", "Customers", "Users"],
  ["/app/pipeline", "Pipeline", "GitBranch"],
  ["/app/appointments", "Appointments", "CalendarCheck"],
  ["/app/calls", "Calls", "PhoneCall"],
  ["/app/messages", "Messages", "MessagesSquare"],
  ["/app/tasks", "Tasks", "ListChecks"],
  ["/app/automations", "Automations", "Workflow"],
  ["/app/ai-agents", "AI Agents", "Bot"],
  ["/app/analytics", "Analytics", "BarChart3"],
  ["/app/payments", "Payments", "CreditCard"],
  ["/app/team", "Team", "UserCog"],
  ["/app/integrations", "Integrations", "Plug"],
  ["/app/settings", "Settings", "Settings"],
];

const ROLE_HIDDEN = {
  staff: ["/app/payments", "/app/team", "/app/integrations", "/app/settings"],
  manager: ["/app/integrations"],
};

export default function AppLayout() {
  const { user, org, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [search, setSearch] = useState("");

  const nav = useMemo(() => {
    const hidden = user?.is_superadmin ? [] : ROLE_HIDDEN[user?.role] || [];
    return NAV.filter(([path]) => !hidden.includes(path));
  }, [user]);

  const loadNotifications = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data);
    } catch (e) {
      setNotifications([]);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const unread = notifications.filter((n) => !n.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setNotifications((n) => n.map((x) => ({ ...x, read: true })));
    toast.success("Notifications marked as read.");
  };

  const doSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    navigate(`/app/customers?q=${encodeURIComponent(search.trim())}`);
  };

  return (
    <div className="min-h-screen bg-elv-base">
      {org?.demo_mode ? (
        <div
          data-testid="demo-mode-banner"
          className="flex flex-wrap items-center justify-center gap-2 border-b border-elv-cyan/20 bg-elv-cyan/[0.06] px-4 py-2 text-center text-[11px] text-elv-cyan"
        >
          <Icons.FlaskConical className="h-3.5 w-3.5" />
          Demo Environment — {org.name}. All contacts, calls, appointments and revenue shown are fictional
          demonstration data.
        </div>
      ) : null}

      <div className="flex">
        {/* Sidebar */}
        <aside
          data-testid="app-sidebar"
          className={`fixed inset-y-0 left-0 z-50 w-[248px] border-r border-elv-line bg-[#080B10] transition-transform duration-300 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex h-14 items-center justify-between border-b border-elv-line px-4">
            <Wordmark compact testId="app-brand" />
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              data-testid="sidebar-close"
              className="text-elv-dim lg:hidden"
              aria-label="Close sidebar"
            >
              <Icons.X className="h-4 w-4" />
            </button>
          </div>
          <nav className="scrollbar-thin grid gap-0.5 overflow-y-auto p-3" style={{ maxHeight: "calc(100vh - 8rem)" }}>
            {nav.map(([to, label, icon]) => {
              const Icon = Icons[icon] || Icons.Circle;
              return (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={`sidebar-link-${to.split("/").pop()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] transition-colors duration-200 ${
                      isActive
                        ? "border border-elv-green/25 bg-elv-green/[0.08] text-elv-text"
                        : "border border-transparent text-elv-dim hover:bg-white/[0.04] hover:text-elv-text"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              );
            })}
            {user?.is_superadmin ? (
              <Link
                to="/admin"
                data-testid="sidebar-link-admin"
                className="mt-2 flex items-center gap-2.5 rounded-md border border-elv-cyan/25 bg-elv-cyan/[0.06] px-3 py-2 text-[13px] text-elv-cyan"
              >
                <Icons.ShieldCheck className="h-4 w-4" />
                Super Admin
              </Link>
            ) : null}
          </nav>
          <div className="absolute inset-x-0 bottom-0 border-t border-elv-line p-3">
            <Link
              to="/"
              data-testid="sidebar-public-site"
              className="flex items-center gap-2 rounded-md px-3 py-2 text-xs text-elv-dim hover:text-elv-text"
            >
              <Icons.Globe className="h-3.5 w-3.5" /> Public site
            </Link>
          </div>
        </aside>

        {sidebarOpen ? (
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          />
        ) : null}

        {/* Main */}
        <div className="min-w-0 flex-1">
          <header
            data-testid="app-topbar"
            className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-elv-line bg-[#05070A]/90 px-4 backdrop-blur-xl"
          >
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              data-testid="sidebar-open"
              className="text-elv-dim lg:hidden"
              aria-label="Open sidebar"
            >
              <Icons.Menu className="h-5 w-5" />
            </button>

            <form onSubmit={doSearch} className="relative hidden flex-1 max-w-md sm:block">
              <Icons.Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-elv-dim" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search customers, leads, invoices…"
                data-testid="topbar-search"
                className="w-full rounded-md border border-elv-line bg-white/[0.02] py-2 pl-9 pr-3 text-xs text-elv-text outline-none focus:border-elv-green/40"
              />
            </form>

            <div className="ml-auto flex items-center gap-2">
              <div
                data-testid="business-switcher"
                className="hidden items-center gap-2 rounded-md border border-elv-line bg-white/[0.02] px-3 py-2 text-xs text-elv-dim md:flex"
              >
                <Icons.Building2 className="h-3.5 w-3.5 text-elv-green" />
                <span className="max-w-[160px] truncate text-elv-text">{org?.name || "—"}</span>
                <Icons.ChevronDown className="h-3 w-3" />
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setNotifOpen((v) => !v)}
                  data-testid="notifications-button"
                  className="relative rounded-md border border-elv-line bg-white/[0.02] p-2 text-elv-dim hover:text-elv-text"
                  aria-label="Notifications"
                >
                  <Icons.Bell className="h-4 w-4" />
                  {unread ? (
                    <span
                      data-testid="notifications-badge"
                      className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-elv-green px-1 text-[9px] font-bold text-[#04120A]"
                    >
                      {unread}
                    </span>
                  ) : null}
                </button>
                {notifOpen ? (
                  <div
                    data-testid="notifications-panel"
                    className="absolute right-0 top-11 z-50 w-[320px] rounded-lg border border-elv-line bg-[#0A0F14] p-3 shadow-2xl"
                  >
                    <div className="flex items-center justify-between border-b border-elv-line pb-2">
                      <p className="text-xs uppercase tracking-[0.18em] text-elv-dim">Notifications</p>
                      <button
                        type="button"
                        onClick={markAll}
                        data-testid="notifications-mark-all"
                        className="text-[11px] text-elv-green hover:text-elv-cyan"
                      >
                        Mark all read
                      </button>
                    </div>
                    <div className="scrollbar-thin mt-2 max-h-72 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p data-testid="notifications-empty" className="px-1 py-6 text-center text-xs text-elv-dim">
                          Nothing yet. New leads, bookings and escalations will appear here.
                        </p>
                      ) : (
                        notifications.map((n) => (
                          <div
                            key={n.id}
                            data-testid={`notification-${n.id}`}
                            className={`rounded-md px-2.5 py-2.5 ${n.read ? "" : "bg-elv-green/[0.05]"}`}
                          >
                            <p className="text-xs font-medium text-elv-text">{n.title}</p>
                            <p className="mt-1 text-[11px] text-elv-dim">{n.body}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="flex items-center gap-2 rounded-md border border-elv-line bg-white/[0.02] px-2.5 py-1.5">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-elv-green/15 text-[10px] font-bold text-elv-green">
                  {(user?.name || "?").slice(0, 1).toUpperCase()}
                </span>
                <span data-testid="topbar-user" className="hidden text-xs text-elv-text sm:block">
                  {user?.name}
                </span>
                <span className="hidden text-[10px] uppercase tracking-[0.14em] text-elv-dim md:block">
                  {user?.role}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                  data-testid="logout-button"
                  className="ml-1 text-elv-dim hover:text-red-300"
                  aria-label="Sign out"
                >
                  <Icons.LogOut className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </header>

          <main className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
